import { GetStaticProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useState, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import clsx from 'clsx'
import { getPostsWithSettingsQueryHelpers } from 'data/posts'
import { MainLayout } from 'components/layouts'
import { PostCard } from 'components/blog'
import { Newsletter } from 'components/ui'
import type { BlogPage, Post as PostType } from 'utils/types/sanity/post'
import { config } from 'config/config'

const { fetch, usePreviewSubscription } = getPostsWithSettingsQueryHelpers()

interface BlogProps {
  data: {
    page: BlogPage
  }
  preview: boolean
}

const Blog: NextPage<BlogProps> = ({ data, preview }) => {
  const { data: page } = usePreviewSubscription(data.page, {}, preview)
  const [active, setActive] = useState('all')

  const posts = useMemo<PostType[]>(
    () => [page.all, ...page.categories].find(({ _id }) => _id === active)?.posts || [],
    [page.all, page.categories, active]
  )

  return (
    <MainLayout
      title='Hypzz Blog'
      settings={page.websiteSettings}
      path='/blog'
      preview={preview}
    >
      <div className='bg-white'>
        <div className='bg-[#FDFAFF]'>
          <div className='container grid w-auto grid-cols-4 mx-4 auto-rows-auto md:mx-auto md:grid-cols-12 gap-x-6'>
            <div className='col-span-full md:col-start-3 md:col-span-8'>
              <header className='flex items-center justify-between py-8'>
                <Link href='/' passHref>
                  <a>
                    <Image
                      src='/logo.svg'
                      height={40}
                      width={118.39}
                      title='Hypzz'
                      alt='logo'
                    />
                  </a>
                </Link>
                <Link href='/' passHref>
                  <a className='bg-[#A26CFF] py-1 px-2 rounded text-gray-100 hover:bg-[#A46FFF]'>
                    Home
                  </a>
                </Link>
              </header>
              <h1 className='mt-8 text-5xl font-semibold text-navy-900'>blog.</h1>
              <p className='mt-8 text-gray-500 uppercase font-accent'>
                Filter by category
              </p>
              <div className='flex mt-4 mb-8 space-x-4 overflow-hidden'>
                {[page.all, ...page.categories].map(({ _id, title }) => (
                  <button
                    key={_id}
                    className={clsx(
                      'text-navy-900',
                      _id === active ? 'font-semibold' : ''
                    )}
                    onClick={() => setActive(_id)}
                  >
                    {title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className='container grid w-auto grid-cols-4 py-24 mx-4 auto-rows-auto md:mx-auto md:grid-cols-12 gap-x-6'>
          <ul className='grid gap-12 lg:gap-16 col-span-full md:col-start-3 md:col-span-8 sm:grid-cols-2'>
            {posts.map((p) => (
              <li key={p._id}>
                <PostCard {...p} />
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Newsletter />
    </MainLayout>
  )
}

export const getStaticProps: GetStaticProps<BlogProps> = async ({
  preview = false,
  locale = config.defaultLocale,
}) => {
  const page = await fetch({}, preview)

  return {
    props: {
      data: { page },
      preview,
      ...(await serverSideTranslations(locale, ['common', 'footer'])),
    },
    revalidate: config.next.pageRevalidateDelay,
  }
}

export default Blog
